import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, XCircle, Loader2, Clock, ChevronDown } from 'lucide-react'
import { cn } from '@/utils'

export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped'

export interface TaskStep {
  id: string
  name: string
  display_name: string
  status: StepStatus
  progress: number
  message?: string
  icon: string
  started_at?: string
  completed_at?: string
}

export interface TaskQueue {
  id: string
  title: string
  steps: TaskStep[]
  status: StepStatus
  progress: number
  conversation_id?: string
}

interface TaskTrackerProps {
  queue: TaskQueue | null
  onToggleCollapse?: () => void
}

const statusColors: Record<StepStatus, string> = {
  pending: 'text-white/40',
  in_progress: 'text-primary',
  completed: 'text-success',
  failed: 'text-danger',
  skipped: 'text-white/30',
}

const statusBgColors: Record<StepStatus, string> = {
  pending: 'bg-white/5',
  in_progress: 'bg-primary/10',
  completed: 'bg-success/10',
  failed: 'bg-danger/10',
  skipped: 'bg-white/5',
}

function StatusIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case 'pending':
      return <Clock className="w-4 h-4 animate-pulse" />
    case 'in_progress':
      return <Loader2 className="w-4 h-4 animate-spin" />
    case 'completed':
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 20 }}
        >
          <CheckCircle2 className="w-4 h-4" />
        </motion.div>
      )
    case 'failed':
      return (
        <motion.div
          initial={{ x: -5 }}
          animate={{ x: [0, -3, 3, -3, 3, 0] }}
          transition={{ duration: 0.4 }}
        >
          <XCircle className="w-4 h-4" />
        </motion.div>
      )
    default:
      return <Clock className="w-4 h-4 opacity-30" />
  }
}

function TaskStepRow({ step }: { step: TaskStep }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={cn(
        'flex items-center gap-3 py-2 px-3 rounded-lg transition-all duration-300',
        statusBgColors[step.status]
      )}
    >
      {/* Icon */}
      <span className="text-lg" role="img" aria-label={step.name}>
        {step.icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm font-medium truncate',
              step.status === 'in_progress' ? 'text-white' : 'text-white/70'
            )}
          >
            {step.message || step.display_name}
          </span>
        </div>

        {/* Progress bar for in_progress */}
        {step.status === 'in_progress' && step.progress > 0 && step.progress < 100 && (
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            className="h-1 mt-1 rounded-full bg-white/10 overflow-hidden origin-left"
          >
            <motion.div
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${step.progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        )}
      </div>

      {/* Status icon */}
      <div className={cn('flex-shrink-0', statusColors[step.status])}>
        <StatusIcon status={step.status} />
      </div>
    </motion.div>
  )
}

export function TaskTracker({ queue, onToggleCollapse }: TaskTrackerProps) {
  const [isExpanded, setIsExpanded] = useState(true) // Always start expanded

  useEffect(() => {
    // Auto-expand when new queue arrives or status changes
    if (queue) {
      setIsExpanded(true)
    }
  }, [queue?.id, queue?.status])

  if (!queue || queue.steps.length === 0) {
    return null
  }

  const completedCount = queue.steps.filter((s) => s.status === 'completed').length
  const totalCount = queue.steps.length
  const currentStep = queue.steps.find((s) => s.status === 'in_progress')
  const isComplete = queue.status === 'completed'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mb-4"
    >
      <div
        className={cn(
          'rounded-xl overflow-hidden backdrop-blur-md transition-all duration-300',
          isComplete ? 'bg-success/10 border border-success/20' : 'bg-white/5 border border-white/10'
        )}
      >
        {/* Header */}
        <button
          onClick={() => {
            setIsExpanded(!isExpanded)
            onToggleCollapse?.()
          }}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            {/* Overall status indicator */}
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center',
                isComplete ? 'bg-success/20' : 'bg-primary/20'
              )}
            >
              {isComplete ? (
                <CheckCircle2 className="w-4 h-4 text-success" />
              ) : (
                <Loader2 className="w-4 h-4 text-primary animate-spin" />
              )}
            </div>

            <div className="text-left">
              <p className="text-sm font-medium text-white">
                {isComplete
                  ? 'Completed'
                  : currentStep
                    ? currentStep.display_name
                    : 'Processing...'}
              </p>
              <p className="text-xs text-white/50">
                {completedCount} of {totalCount} steps
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Mini progress */}
            <div className="hidden sm:flex items-center gap-1">
              {queue.steps.map((step) => (
                <div
                  key={step.id}
                  className={cn(
                    'w-2 h-2 rounded-full transition-all duration-300',
                    step.status === 'completed'
                      ? 'bg-success'
                      : step.status === 'in_progress'
                        ? 'bg-primary animate-pulse'
                        : step.status === 'failed'
                          ? 'bg-danger'
                          : 'bg-white/20'
                  )}
                />
              ))}
            </div>

            {/* Expand/collapse */}
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              className="text-white/40"
            >
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </div>
        </button>

        {/* Steps list */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-3 pb-3 space-y-1">
                <AnimatePresence mode="popLayout">
                  {queue.steps.map((step) => (
                    <TaskStepRow key={step.id} step={step} />
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

// Compact version for inline display
export function TaskTrackerCompact({ queue }: { queue: TaskQueue | null }) {
  if (!queue || queue.steps.length === 0) {
    return null
  }

  const currentStep = queue.steps.find((s) => s.status === 'in_progress')
  const isComplete = queue.status === 'completed'

  if (isComplete) {
    return null // Hide when complete
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20"
    >
      <span className="text-base">{currentStep?.icon || '⚙️'}</span>
      <span className="text-xs text-white/80">{currentStep?.message || 'Working...'}</span>
      <Loader2 className="w-3 h-3 text-primary animate-spin" />
    </motion.div>
  )
}

export default TaskTracker
